import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FadeIn from '../components/FadeIn';
import { seoPagesData } from '../data/seoPages';

const anchorPattern = /<a\s+href=['"]([^'"]+)['"]>([\s\S]*?)<\/a>/gi;

function renderAuthoredText(text, keyPrefix) {
  if (typeof text !== 'string') return text;

  const segments = [];
  let lastIndex = 0;
  let match;
  let segmentIndex = 0;

  anchorPattern.lastIndex = 0;

  while ((match = anchorPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(text.slice(lastIndex, match.index));
    }

    const [, href, label] = match;
    const key = `${keyPrefix}-${segmentIndex}`;

    if (href.startsWith('/')) {
      segments.push(
        <Link key={key} to={href} className="text-brand-olive underline underline-offset-4 hover:text-brand-charcoal">
          {label}
        </Link>
      );
    } else {
      segments.push(
        <a
          key={key}
          href={href}
          className="text-brand-olive underline underline-offset-4 hover:text-brand-charcoal"
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </a>
      );
    }

    lastIndex = anchorPattern.lastIndex;
    segmentIndex += 1;
  }

  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments.length ? segments : text;
}

export default function SeoTemplatePage({ pageId }) {
  const { slug } = useParams();
  const id = pageId || slug;
  const data = seoPagesData[id];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-vintage">
        <SeoMeta
          title="Page Not Found | Padua Weddings"
          description="The requested Padua Weddings page could not be found."
          noindex
        />
        <h1 className="font-serif text-3xl text-brand-charcoal">Page not found.</h1>
      </div>
    );
  }

  return (
    <main className="bg-brand-vintage pb-0">
      <SeoMeta title={data.title} description={data.description} />
      <PageHero
        title={data.heroTitle}
        subtitle={data.heroSubtitle}
        image={data.heroImage}
      />
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto space-y-24">
          {data.sections.map((section, index) => (
            <FadeIn
              key={section.heading}
              className={`flex flex-col gap-8 ${
                index % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'
              } items-center`}
            >
              {section.image && (
                <div className="w-full md:w-1/2">
                  <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-xl">
                    <img
                      src={section.image}
                      alt={section.heading}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
              <div className={`w-full ${section.image ? 'md:w-1/2' : 'max-w-3xl mx-auto text-center'}`}>
                <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-6 font-light">
                  {section.heading}
                </h2>
                <p className="font-sans text-brand-charcoal/80 text-lg leading-relaxed font-light">
                  {renderAuthoredText(section.text, `section-${index}`)}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>
      <CTASection />
    </main>
  );
}